import { action, makeObservable } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import { HttpError } from '../../../app/data/HttpError';
import { Form, TextInput, ValueMaxLength, ValueRequiredRule } from '../../../app/shared/Form';
import { PricelistFormTranslation } from '../../localization/ModuleTranslation';
import type { CustomerPricelistData } from '../../model';

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 150;

/** A name already taken. The one failure the form can point at a field rather than report as a general error. */
const NAME_CONFLICT_STATUS = 409;

export interface PricelistFormResult {
  isSuccess: boolean;
  errorMessage?: string;
  /** The list as the API stored it. */
  pricelist?: CustomerPricelistData;
}

/**
 * A price list's name and description: the fields, their rules and how a failure maps onto them.
 * What submitting does is the subclass's — `CreatePricelistFormState` or `EditPricelistFormState`.
 */
export default abstract class PricelistFormState extends Form {
  public readonly name: TextInput;
  public readonly description: TextInput;

  constructor(
    protected readonly client: IApiClient,
    public readonly translation: PricelistFormTranslation,
  ) {
    super();
    makeObservable(this);

    this.name = new TextInput()
      .withRule(new ValueRequiredRule(this.translation.name.requiredError))
      .withRule(new ValueMaxLength(NAME_MAX_LENGTH, this.translation.name.maxLengthError));
    this.description = new TextInput().withRule(
      new ValueMaxLength(DESCRIPTION_MAX_LENGTH, this.translation.description.maxLengthError),
    );

    this.inputsToValidate.push(this.name, this.description);
  }

  public abstract submit(): Promise<PricelistFormResult>;

  @action
  public reset(pricelist?: CustomerPricelistData): void {
    this.resetValidation();
    this.name.value = pricelist?.name || '';
    this.description.value = pricelist?.description || '';
  }

  /** A taken name is pointed at the name field; anything else is the caller's `genericError`. */
  protected toFailure(error: unknown, genericError: string): PricelistFormResult {
    if (error instanceof HttpError && error.status === NAME_CONFLICT_STATUS) {
      this.setNameError(this.translation.errors.nameAlreadyExists);

      return { isSuccess: false };
    }

    return { isSuccess: false, errorMessage: genericError };
  }

  @action
  private setNameError(errorMessage: string): void {
    this.name.brokenRules = [{ errorMessage, validate: () => false }];
  }
}
